import { useCallback, useEffect, useMemo } from 'react';
import { AppDispatch, RootState } from '@/store';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import {
  useGetCreditScoreAdjustmentsQuery,
  useGetCurrentRatesQuery,
  useGetLTVAdjustmentsQuery,
} from '@/apis/extended/userInterestRateApi';
import {
  HybridBackendRates,
  markCacheStale,
  resetAllLocalAdjustments,
  resetLocalAdjustments,
  selectBackendRates,
  selectCreditScoreAdjustments,
  selectEffectiveRate,
  selectError,
  selectIsStale,
  selectLoading,
  selectLocalAdjustments,
  selectLTVAdjustments,
  selectNeedsRefresh,
  selectRateSheet,
  setBackendRates,
  setCreditScoreAdjustments,
  setLocalAdjustment,
  setLTVAdjustments,
} from '@/store/slices/hybridInterestRateSlice';
import { getBridgeRateByTerm } from '@/utils/calculator/rateIntegration';
import { priceProgram } from '@/utils/rateEngine';

/** USER_LOAN_PROGRAM -> backend program key on the rate sheet. */
export const PROGRAM_KEY_BY_USER_PROGRAM: Record<USER_LOAN_PROGRAM, string> = {
  [USER_LOAN_PROGRAM.DEFAULT]: 'conventional',
  [USER_LOAN_PROGRAM.CONVENTIONAL]: 'conventional',
  [USER_LOAN_PROGRAM.FHA]: 'fha',
  [USER_LOAN_PROGRAM.VA]: 'va',
  [USER_LOAN_PROGRAM.USDA]: 'usda',
  [USER_LOAN_PROGRAM.DSCR]: 'dscr',
  [USER_LOAN_PROGRAM.COMMERCIAL]: 'commercial',
  [USER_LOAN_PROGRAM.FIX_AND_FLIP]: 'fixAndFlip',
  [USER_LOAN_PROGRAM.BRIDGE]: 'bridge',
  [USER_LOAN_PROGRAM.CASH]: 'cash',
  [USER_LOAN_PROGRAM.HELOC]: 'heloc',
  [USER_LOAN_PROGRAM.HELOAN]: 'heloan',
  [USER_LOAN_PROGRAM.SECOND_MORTGAGE]: 'secondMortgage',
};

/** Optional bridge term (months) so BRIDGE uses term-tier pricing like ProductDetail / quote.ts */
export type HybridRateOptions = {
  bridgeTermMonths?: number;
  /** Loan term in years (15-year terms get the market 15/30 spread). */
  loanTerm?: number;
};

export const useHybridInterestRate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();

  // Selectors
  const backendRates = useSelector(selectBackendRates);
  const rateSheet = useSelector(selectRateSheet);
  const localAdjustments = useSelector(selectLocalAdjustments);
  const creditScoreAdjustments = useSelector(selectCreditScoreAdjustments);
  const ltvAdjustments = useSelector(selectLTVAdjustments);
  const needsRefresh = useSelector(selectNeedsRefresh);
  const isStale = useSelector(selectIsStale);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  // API queries with caching
  const {
    data: currentRates,
    isLoading: ratesLoading,
    error: ratesError,
    refetch: refetchRates,
  } = useGetCurrentRatesQuery(undefined, {
    // Always subscribe when hook mounts; RTK Query dedupes. Redux cache (selectNeedsRefresh)
    // still used by refreshRates(); do not skip initial fetch.
    skip: false,
    pollingInterval: 5 * 60 * 1000,
  });

  const {
    data: creditScoreData,
    isLoading: creditScoreLoading,
    error: creditScoreError,
  } = useGetCreditScoreAdjustmentsQuery(undefined, {
    // Only fetch once, then cache
    skip: !!creditScoreAdjustments,
  });

  const {
    data: ltvData,
    isLoading: ltvLoading,
    error: ltvError,
  } = useGetLTVAdjustmentsQuery(undefined, {
    // Only fetch once, then cache
    skip: !!ltvAdjustments,
  });

  // Update backend rates when API returns data
  useEffect(() => {
    if (currentRates) {
      const lastLogged = (window as any).__lastHybridRateLog;
      const shouldLog = !lastLogged || lastLogged !== currentRates.lastUpdated;

      if (shouldLog) {
        console.log(
          '[ProductDetail Rate Update] Backend rates fetched and updated',
          {
            rates: currentRates,
            lastUpdated: currentRates.lastUpdated,
            timestamp: new Date().toISOString(),
            source: 'backend-api',
          }
        );
        (window as any).__lastHybridRateLog = currentRates.lastUpdated;
      }
      dispatch(setBackendRates(currentRates));
    }
  }, [currentRates, dispatch]);

  // Update credit score adjustments when API returns data
  useEffect(() => {
    if (creditScoreData) {
      dispatch(setCreditScoreAdjustments(creditScoreData));
    }
  }, [creditScoreData, dispatch]);

  // Update LTV adjustments when API returns data
  useEffect(() => {
    if (ltvData) {
      dispatch(setLTVAdjustments(ltvData));
    }
  }, [ltvData, dispatch]);

  const backendRateKeyMap: Record<USER_LOAN_PROGRAM, keyof HybridBackendRates> =
    {
      [USER_LOAN_PROGRAM.DEFAULT]: 'conventional',
      [USER_LOAN_PROGRAM.CONVENTIONAL]: 'conventional',
      [USER_LOAN_PROGRAM.FHA]: 'fha',
      [USER_LOAN_PROGRAM.VA]: 'va',
      [USER_LOAN_PROGRAM.USDA]: 'usda',
      [USER_LOAN_PROGRAM.DSCR]: 'dscr',
      [USER_LOAN_PROGRAM.COMMERCIAL]: 'commercial',
      [USER_LOAN_PROGRAM.FIX_AND_FLIP]: 'fixAndFlip',
      [USER_LOAN_PROGRAM.BRIDGE]: 'bridge',
      [USER_LOAN_PROGRAM.CASH]: 'conventional', // unused — Cash short-circuits to 0
      [USER_LOAN_PROGRAM.HELOC]: 'conventional',
      [USER_LOAN_PROGRAM.HELOAN]: 'conventional',
      [USER_LOAN_PROGRAM.SECOND_MORTGAGE]: 'conventional',
    } as const;

  const resolveBackendRate = (
    loanProgram: USER_LOAN_PROGRAM
  ): number | null => {
    const key = backendRateKeyMap[loanProgram] || 'conventional';
    const rate = backendRates[key];
    if (typeof rate === 'number' && Number.isFinite(rate)) {
      return rate;
    }
    return null;
  };

  /** getBridgeRateByTerm expects LTV as 0–1; hybrid UI often passes 0–100 */
  const toLtvDecimal = (ltv: number) => (ltv > 1 ? ltv / 100 : ltv);

  // Set local adjustment for a loan program
  const setAdjustment = useCallback(
    (
      loanProgram: USER_LOAN_PROGRAM,
      adjustment: {
        baseAdjustment?: number;
        creditScoreAdjustment?: number;
        ltvAdjustment?: number;
        customRate?: number;
      }
    ) => {
      dispatch(setLocalAdjustment({ loanProgram, adjustment }));
    },
    [dispatch]
  );

  // Reset local adjustments for a loan program
  const resetAdjustments = useCallback(
    (loanProgram: USER_LOAN_PROGRAM) => {
      dispatch(resetLocalAdjustments(loanProgram));
    },
    [dispatch]
  );

  // Reset all local adjustments
  const resetAllAdjustments = useCallback(() => {
    dispatch(resetAllLocalAdjustments());
  }, [dispatch]);

  // Force refresh of backend rates
  const refreshRates = useCallback(() => {
    dispatch(markCacheStale());
    refetchRates();
  }, [dispatch, refetchRates]);

  // Calculate adjusted rate for a specific scenario
  // NOTE: This function should NOT be used in product detail - backend controls rates
  const calculateAdjustedRate = useCallback(
    (
      loanProgram: USER_LOAN_PROGRAM,
      creditScore: number,
      ltv: number,
      options?: HybridRateOptions
    ): number | null => {
      if (loanProgram === USER_LOAN_PROGRAM.CASH) {
        return 0;
      }

      let baseRate: number | null = null;
      const term =
        options?.bridgeTermMonths != null &&
        Number.isFinite(options.bridgeTermMonths)
          ? Math.round(options.bridgeTermMonths)
          : null;

      if (loanProgram === USER_LOAN_PROGRAM.BRIDGE && term != null) {
        const byTerm = getBridgeRateByTerm(
          store.getState(),
          term,
          creditScore,
          toLtvDecimal(ltv)
        );
        baseRate =
          typeof byTerm === 'number' && Number.isFinite(byTerm) ? byTerm : null;
      }

      const adjustmentsForProgram = localAdjustments[loanProgram];
      // Primary path: price credit score + LTV on the live rate sheet with
      // the same engine the backend quotes with. (This used to return the
      // bare base rate and ignore creditScore/ltv entirely.)
      if (
        baseRate === null &&
        rateSheet &&
        adjustmentsForProgram?.customRate === undefined
      ) {
        const priced = priceProgram(
          rateSheet,
          PROGRAM_KEY_BY_USER_PROGRAM[loanProgram] || 'conventional',
          { creditScore, ltv, loanTerm: options?.loanTerm }
        );
        if (priced) return priced.rate;
      }

      if (baseRate === null) {
        baseRate = resolveBackendRate(loanProgram);
      }

      const adjustments = localAdjustments[loanProgram];

      if (adjustments && Object.keys(adjustments).length > 0) {
        console.warn(
          '[ProductDetail Rate Warning] Local adjustments detected - backend should control rates',
          {
            loanProgram,
            baseRate,
            adjustments,
            warning: 'Local adjustments should not be used in product detail',
          }
        );
      }

      if (adjustments?.customRate !== undefined) {
        return adjustments.customRate;
      }

      if (baseRate === null) {
        return null;
      }

      if (!adjustments) {
        return baseRate;
      }

      let effectiveRate = baseRate;

      // Apply local adjustments (should not be used in product detail)
      if (adjustments.baseAdjustment) {
        effectiveRate += adjustments.baseAdjustment;
      }

      if (adjustments.creditScoreAdjustment) {
        effectiveRate += adjustments.creditScoreAdjustment;
      }

      if (adjustments.ltvAdjustment) {
        effectiveRate += adjustments.ltvAdjustment;
      }

      // Apply credit score adjustments from backend
      if (creditScoreAdjustments) {
        if (creditScore >= 760)
          effectiveRate += creditScoreAdjustments.excellent;
        else if (creditScore >= 740)
          effectiveRate += creditScoreAdjustments.good;
        else if (creditScore >= 720)
          effectiveRate += creditScoreAdjustments.fair;
        else if (creditScore >= 700) effectiveRate += 0;
        else if (creditScore >= 680) effectiveRate += 0.125;
        else if (creditScore >= 660) effectiveRate += 0.25;
        else if (creditScore >= 640) effectiveRate += 0.375;
        else if (creditScore >= 620) effectiveRate += 0.5;
        else if (creditScore >= 600) effectiveRate += 0.625;
        else if (creditScore >= 580) effectiveRate += 0.75;
        else if (creditScore >= 560) effectiveRate += 0.875;
        else if (creditScore >= 540) effectiveRate += 1.0;
        else if (creditScore >= 520) effectiveRate += 1.125;
        else if (creditScore >= 500) effectiveRate += 1.25;
        else effectiveRate += 2.5;
      }

      // Apply LTV adjustments from backend
      if (ltvAdjustments) {
        const ltvPct = ltv > 1 ? ltv : ltv * 100;
        if (ltvPct <= 60) effectiveRate += ltvAdjustments.low;
        else if (ltvPct <= 70) effectiveRate += ltvAdjustments.moderate;
        else if (ltvPct <= 80) effectiveRate += ltvAdjustments.high;
        else if (ltvPct <= 90) effectiveRate += ltvAdjustments.veryHigh;
        else effectiveRate += 0.5; // Default for very high LTV
      }

      return Math.max(0, effectiveRate);
    },
    [
      store,
      backendRates,
      rateSheet,
      localAdjustments,
      creditScoreAdjustments,
      ltvAdjustments,
    ]
  );

  // Memoized values
  const isLoading = useMemo(
    () => loading || ratesLoading || creditScoreLoading || ltvLoading,
    [loading, ratesLoading, creditScoreLoading, ltvLoading]
  );

  const hasError = useMemo(
    () => error || ratesError || creditScoreError || ltvError,
    [error, ratesError, creditScoreError, ltvError]
  );

  return {
    // State
    backendRates,
    localAdjustments,
    creditScoreAdjustments,
    ltvAdjustments,
    needsRefresh,
    isStale,
    loading: isLoading,
    error: hasError,

    // Actions
    setAdjustment,
    resetAdjustments,
    resetAllAdjustments,
    refreshRates,
    calculateAdjustedRate,

    // Raw selectors for components that need them
    selectors: {
      backendRates: () => useSelector(selectBackendRates),
      localAdjustments: () => useSelector(selectLocalAdjustments),
      effectiveRate: (loanProgram: USER_LOAN_PROGRAM) =>
        useSelector(selectEffectiveRate(loanProgram)),
      needsRefresh: () => useSelector(selectNeedsRefresh),
    },
  };
};
